// Small per-feature trend chart (sleep duration, HR, steps, etc.) used by the
// "what-if" simulation sliders. `simulationParams` mirrors the exact transform
// getPHQ8Prediction.ts's backend handler applies server-side (level shift then
// regularity compression around the series mean) — this client-side preview must stay
// numerically identical to that server logic or the chart would mislead the "sim" value.
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"
import { PHQ8FeatureSeriesPoint, ValueType, ValueTypeValueConverters } from "pokal_api"

type SimulationParams = {
    levelDelta: number
    regularityFactor: number
}

type FeatureMiniChartProps = {
    label: string
    valueType: ValueType
    points: PHQ8FeatureSeriesPoint[]
    simulationParams?: SimulationParams
    minDate?: string | null
}

function formatPointDate(date: PHQ8FeatureSeriesPoint["date"]): string {
    const parsed = date instanceof Date ? date : new Date(date)
    if (Number.isNaN(parsed.getTime())) return String(date)
    return parsed.toISOString().split("T")[0]
}

function FeatureMiniChart({ label, valueType, points, simulationParams, minDate }: FeatureMiniChartProps) {
    const { levelDelta = 0, regularityFactor = 1 } = simulationParams ?? {}
    const hasSimulation = levelDelta !== 0 || regularityFactor !== 1

    // Restrict the chart to the lookback windows of currently displayed
    // future predictions; the backend always ships lookback + horizon days,
    // but when fewer future days are visible the leading dates are unused.
    const scopedPoints = minDate
        ? points.filter((p) => formatPointDate(p.date) >= minDate)
        : points

    const validValues = scopedPoints
        .map((p) => p.value)
        .filter((v): v is number => v !== null)
    const seriesMean =
        validValues.length > 0
            ? validValues.reduce((a, b) => a + b, 0) / validValues.length
            : 0

    // Trim leading and trailing nulls so the X-axis only spans dates with real data.
    // Interior nulls are kept to render as visible gaps in the line.
    let trimStart = 0
    let trimEnd = scopedPoints.length - 1
    while (trimStart <= trimEnd && scopedPoints[trimStart].value === null) trimStart++
    while (trimEnd >= trimStart && scopedPoints[trimEnd].value === null) trimEnd--
    const trimmedPoints = scopedPoints.slice(trimStart, trimEnd + 1)

    const chartPoints = trimmedPoints.map((point) => ({
        date: formatPointDate(point.date),
        value: point.value,
        simulatedValue:
            point.value !== null
                ? Math.max(
                      0,
                      seriesMean +
                          (point.value - seriesMean) * regularityFactor +
                          levelDelta
                  )
                : null
    }))

    const latestValue = validValues.length > 0 ? validValues[validValues.length - 1] : null
    const latestSimulatedValue =
        chartPoints.length > 0 ? chartPoints[chartPoints.length - 1].simulatedValue : null

    return (
        <div className={"rounded-lg border border-gray-200 bg-white p-3 shadow-sm"}>
            <div className={"mb-2 flex items-start justify-between gap-3"}>
                <div>
                    <h4 className={"text-sm font-semibold text-gray-900"}>{label}</h4>
                </div>
                <div className={"text-right"}>
                    <div className={"text-sm font-bold text-gray-900"}>
                        {latestValue !== null ? ValueTypeValueConverters[valueType](latestValue) : "-"}
                    </div>
                    <div className={"text-xs text-gray-500"}>aktuell</div>
                    {hasSimulation && latestSimulatedValue !== null && (
                        <div className={"mt-1 text-xs font-semibold text-red-700"}>
                            sim: {ValueTypeValueConverters[valueType](Number(latestSimulatedValue.toFixed(0)))}
                        </div>
                    )}
                </div>
            </div>

            {chartPoints.length > 0 ? (
                <div className={"h-24 w-full"}>
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartPoints}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="date" hide />
                            <YAxis hide domain={["auto", "auto"]} />
                            <Tooltip
                                labelFormatter={(labelValue) => String(labelValue)}
                                formatter={(value, name) => {
                                    const valueLabel =
                                        typeof value === "number"
                                            ? ValueTypeValueConverters[valueType](Number(value.toFixed(0)))
                                            : value
                                    const seriesName =
                                        name === "simulatedValue"
                                            ? `${label} (Simulation)`
                                            : `${label} (Aktuell)`
                                    return [valueLabel, seriesName]
                                }}
                            />
                            <Line
                                type="monotone"
                                dataKey="value"
                                stroke="#2563eb"
                                strokeWidth={2}
                                dot={{ r: 2, fill: "#2563eb", strokeWidth: 0 }}
                                activeDot={{ r: 4 }}
                            />
                            {hasSimulation && (
                                <Line
                                    type="monotone"
                                    dataKey="simulatedValue"
                                    stroke="#820002"
                                    strokeWidth={2}
                                    dot={{ r: 2, fill: "#820002", strokeWidth: 0 }}
                                    activeDot={{ r: 4 }}
                                />
                            )}
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            ) : (
                <div
                    className={
                        "flex h-24 items-center justify-center rounded-md bg-gray-50 text-xs text-gray-500"
                    }
                >
                    No data
                </div>
            )}
        </div>
    )
}

export default FeatureMiniChart
